(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var cartState = {
    isOpen: false
  };
  var bodyLockState = {
    locked: false,
    scrollY: 0,
    touchmoveHandler: null
  };

  /* ── 1.2 AbortController tracking ── */
  var headerController = null;
  var quickViewController = null;
  var cartDrawerController = null;

  /* ── 1.1 MutationObserver tracking ── */
  var cartObserverRef = null;

  /* ── 1.3 Cart operation queue ── */
  var cartBusy = false;
  var cartQueue = [];

  function enqueueCartOp(fn) {
    return new Promise(function (resolve, reject) {
      cartQueue.push({ fn: fn, resolve: resolve, reject: reject });
      processCartQueue();
    });
  }

  function processCartQueue() {
    if (cartBusy || !cartQueue.length) return;
    cartBusy = true;
    var op = cartQueue.shift();
    op.fn()
      .then(function (result) { cartBusy = false; op.resolve(result); processCartQueue(); })
      .catch(function (err) { cartBusy = false; op.reject(err); processCartQueue(); });
  }

  /* ── 1.7 Configurable strings ── */
  var S = window.afStrings || {};
  function str(key, fallback) { return (S && S[key]) || fallback; }

  /* ── Utilities ── */

  function money(cents) {
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2
      }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2) + ' \u20AC';
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function closestElement(target, selector) {
    var base = null;
    if (target instanceof Element) {
      base = target;
    } else if (target && target.parentElement instanceof Element) {
      base = target.parentElement;
    }
    if (!base) return null;
    return base.closest(selector);
  }

  /* ── 1.4 Safe JSON parse from fetch ── */
  function safeJson(res) {
    if (!res.ok) throw new Error('http_' + res.status);
    return res.json().catch(function () {
      throw new Error('invalid_json');
    });
  }

  /* ── 1.5 Focus trap utility ── */
  function trapFocus(container) {
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    function handler(e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE));
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handler);
    return { destroy: function () { container.removeEventListener('keydown', handler); } };
  }

  /* ── Body scroll lock ── */

  function setBodyLock(locked) {
    if (locked) {
      if (bodyLockState.locked) return;
      bodyLockState.locked = true;
      bodyLockState.scrollY = window.scrollY || window.pageYOffset || 0;

      /* Compensate scrollbar width on desktop to avoid layout shift */
      var scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarGap > 0) {
        document.body.style.paddingRight = scrollbarGap + 'px';
      }

      /* Use margin-top trick instead of position:fixed to preserve scroll
         position while still allowing modal content to scroll on iOS.
         No touchmove handler — CSS overscroll-behavior prevents leak. */
      document.documentElement.style.setProperty('--af-lock-offset', '-' + bodyLockState.scrollY + 'px');
      document.documentElement.classList.add('af-no-scroll');
      document.body.classList.add('af-no-scroll');
      return;
    }

    if (!bodyLockState.locked) return;
    bodyLockState.locked = false;

    document.documentElement.classList.remove('af-no-scroll');
    document.body.classList.remove('af-no-scroll');
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--af-lock-offset');

    window.scrollTo(0, bodyLockState.scrollY || 0);
  }

  /* ── Header ── */

  function initHeader() {
    var header = document.querySelector('[data-af-header]');
    var toggle = document.querySelector('[data-af-menu-toggle]');
    var menu = document.querySelector('[data-af-mobile-menu]');

    if (!header) return;

    if (headerController) headerController.abort();
    headerController = new AbortController();
    var signal = headerController.signal;

    /* ── Measure fixed bars and set CSS vars ── */
    var announce = document.querySelector('.af-announcement');
    function measureBars() {
      var aH = announce ? announce.offsetHeight : 0;
      var hH = header.offsetHeight || 74;
      document.documentElement.style.setProperty('--af-announce-h', aH + 'px');
      document.documentElement.style.setProperty('--af-header-h', hH + 'px');
      if (!announce || announce.offsetHeight === 0) {
        header.classList.add('af-header--no-announce');
      } else {
        header.classList.remove('af-header--no-announce');
      }
    }
    measureBars();
    window.addEventListener('resize', measureBars, { passive: true, signal: signal });

    var onScroll = function () {
      if (window.scrollY > 24) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true, signal: signal });

    if (toggle && menu) {
      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        menu.classList.toggle('is-open');
      }, { signal: signal });

      menu.querySelectorAll('a').forEach(function (link) {
        link.addEventListener('click', function () {
          toggle.setAttribute('aria-expanded', 'false');
          menu.classList.remove('is-open');
        }, { signal: signal });
      });
    }
  }

  /* ── Before / After sliders (drag-based, Vercel style) ── */

  function initBeforeAfter() {
    /* Drag-based sliders (gallery page) */
    var baSliders = document.querySelectorAll('[data-af-ba-slider]');
    baSliders.forEach(function (stage) {
      var clip = stage.querySelector('[data-af-ba-clip]');
      var divider = stage.querySelector('[data-af-ba-divider]');
      var handle = stage.querySelector('[data-af-ba-handle]');
      if (!clip || !divider || !handle) return;

      var isDragging = false;
      var position = 50;

      function updatePosition(clientX) {
        var rect = stage.getBoundingClientRect();
        var pct = ((clientX - rect.left) / rect.width) * 100;
        position = Math.max(5, Math.min(95, pct));
        clip.style.clipPath = 'inset(0 ' + (100 - position) + '% 0 0)';
        divider.style.left = position + '%';
        handle.style.left = position + '%';
      }

      /* Only start drag from handle or divider — not the entire photo */
      function startDrag(e) {
        e.preventDefault();
        isDragging = true;
        handle.setPointerCapture(e.pointerId);
      }
      handle.addEventListener('pointerdown', startDrag);
      divider.addEventListener('pointerdown', startDrag);

      handle.addEventListener('pointermove', function (e) {
        if (!isDragging) return;
        updatePosition(e.clientX);
      });

      handle.addEventListener('pointerup', function () { isDragging = false; });
      handle.addEventListener('pointercancel', function () { isDragging = false; });
    });
  }

  /* ── Photo gallery (desktop carousel + lightbox) ── */

  function initPhotoGallery() {
    var gallery = document.querySelector('[data-af-photo-gallery]');
    if (!gallery) return;

    var slides = gallery.querySelectorAll('[data-af-photo-slide]');
    var thumbs = gallery.querySelectorAll('[data-af-photo-thumb]');
    var prevBtn = gallery.querySelector('[data-af-photo-prev]');
    var nextBtn = gallery.querySelector('[data-af-photo-next]');

    if (!slides.length) return;

    var currentIndex = 0;

    function goTo(index) {
      currentIndex = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s) { s.classList.remove('is-active'); });
      thumbs.forEach(function (t) { t.classList.remove('is-active'); });
      slides[currentIndex].classList.add('is-active');
      if (thumbs[currentIndex]) {
        thumbs[currentIndex].classList.add('is-active');
        thumbs[currentIndex].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = Number(thumb.getAttribute('data-af-photo-thumb'));
        goTo(idx);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); });

    /* Lightbox */
    var overlay = document.querySelector('[data-af-lightbox-overlay]');
    var content = document.querySelector('[data-af-lightbox-content]');
    var lbImg = document.querySelector('[data-af-lightbox-img]');
    var lbCaption = document.querySelector('[data-af-lightbox-caption]');
    var closeBtn = document.querySelector('[data-af-lightbox-close]');
    var lbPrev = document.querySelector('[data-af-lightbox-prev]');
    var lbNext = document.querySelector('[data-af-lightbox-next]');

    if (!overlay || !content || !lbImg) return;

    var lbItems = [];
    var lbIndex = -1;

    function collectItems() {
      lbItems = [];
      slides.forEach(function (slide) {
        var img = slide.querySelector('img');
        if (!img) return;
        var caption = slide.querySelector('.af-photo-gallery__caption');
        lbItems.push({
          src: img.src.replace(/width=\d+/, 'width=1600'),
          caption: caption ? caption.textContent : ''
        });
      });
    }

    function openLightbox(index) {
      collectItems();
      if (index < 0 || index >= lbItems.length) return;
      lbIndex = index;
      renderLightbox();
      overlay.classList.add('is-open');
      content.classList.add('is-open');
      setBodyLock(true);
    }

    function closeLightbox() {
      overlay.classList.remove('is-open');
      content.classList.remove('is-open');
      setBodyLock(false);
      lbIndex = -1;
    }

    function renderLightbox() {
      if (lbIndex < 0 || !lbItems[lbIndex]) return;
      var item = lbItems[lbIndex];
      lbImg.src = item.src;
      lbImg.alt = item.caption;
      if (lbCaption) lbCaption.textContent = item.caption;
    }

    /* Click main image to open lightbox */
    slides.forEach(function (slide, i) {
      slide.querySelector('img').addEventListener('click', function () {
        openLightbox(i);
      });
    });

    /* Also click feed items on mobile */
    document.querySelectorAll('.af-photo-feed__item img').forEach(function (img, i) {
      img.style.cursor = 'pointer';
      img.addEventListener('click', function () { openLightbox(i); });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    if (overlay) overlay.addEventListener('click', closeLightbox);

    if (lbPrev) lbPrev.addEventListener('click', function (e) {
      e.stopPropagation();
      lbIndex = lbIndex <= 0 ? lbItems.length - 1 : lbIndex - 1;
      renderLightbox();
    });

    if (lbNext) lbNext.addEventListener('click', function (e) {
      e.stopPropagation();
      lbIndex = lbIndex >= lbItems.length - 1 ? 0 : lbIndex + 1;
      renderLightbox();
    });

    document.addEventListener('keydown', function (e) {
      if (lbIndex < 0) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight' && lbNext) lbNext.click();
      if (e.key === 'ArrowLeft' && lbPrev) lbPrev.click();
    });

    /* Swipe gestures for lightbox */
    var lbTouchStartX = 0;
    var lbTouchStartY = 0;
    content.addEventListener('touchstart', function (e) {
      lbTouchStartX = e.changedTouches[0].screenX;
      lbTouchStartY = e.changedTouches[0].screenY;
    }, { passive: true });
    content.addEventListener('touchend', function (e) {
      if (lbIndex < 0) return;
      var dx = e.changedTouches[0].screenX - lbTouchStartX;
      var dy = e.changedTouches[0].screenY - lbTouchStartY;
      if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
      if (dx > 0 && lbPrev) lbPrev.click();
      else if (dx < 0 && lbNext) lbNext.click();
    }, { passive: true });

    /* Swipe on main gallery slides (touch devices) */
    var galleryMain = gallery.querySelector('.af-photo-gallery__main');
    if (galleryMain) {
      var gTouchStartX = 0;
      galleryMain.addEventListener('touchstart', function (e) {
        gTouchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      galleryMain.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].screenX - gTouchStartX;
        if (Math.abs(dx) < 50) return;
        if (dx > 0) goTo(currentIndex - 1);
        else goTo(currentIndex + 1);
      }, { passive: true });
    }
  }

  /* ── Reviews Carousel ── */

  function initReviewsCarousel() {
    var carousel = document.querySelector('[data-af-reviews-carousel]');
    if (!carousel) return;

    var slides = carousel.querySelectorAll('[data-af-review-slide]');
    var dots = carousel.querySelectorAll('[data-af-reviews-goto]');
    var prevBtn = carousel.querySelector('[data-af-reviews-prev]');
    var nextBtn = carousel.querySelector('[data-af-reviews-next]');

    if (!slides.length) return;

    var currentIndex = 0;
    var autoTimer = null;
    var AUTO_INTERVAL = 5800;

    function goTo(index) {
      currentIndex = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s) { s.classList.remove('is-active'); });
      dots.forEach(function (d) { d.classList.remove('is-active'); });
      slides[currentIndex].classList.add('is-active');
      if (dots[currentIndex]) dots[currentIndex].classList.add('is-active');
    }

    function startAuto() {
      stopAuto();
      autoTimer = window.setInterval(function () {
        goTo(currentIndex + 1);
      }, AUTO_INTERVAL);
    }

    function stopAuto() {
      if (autoTimer) { window.clearInterval(autoTimer); autoTimer = null; }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { stopAuto(); goTo(currentIndex - 1); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { stopAuto(); goTo(currentIndex + 1); startAuto(); });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = Number(dot.getAttribute('data-af-reviews-goto'));
        stopAuto();
        goTo(idx);
        startAuto();
      });
    });

    startAuto();
  }

  /* ── Services Accordion ── */

  function initAccordion() {
    var accordion = document.querySelector('[data-af-accordion]');
    if (!accordion) return;

    var items = accordion.querySelectorAll('[data-af-accordion-item]');

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-af-accordion-trigger]');
      var body = item.querySelector('.af-accordion-body');
      if (!trigger || !body) return;

      /* Set initial height for open item */
      if (item.classList.contains('is-open')) {
        body.style.maxHeight = body.scrollHeight + 'px';
      }

      trigger.addEventListener('click', function () {
        var wasOpen = item.classList.contains('is-open');

        /* Close all */
        items.forEach(function (other) {
          other.classList.remove('is-open');
          var otherBody = other.querySelector('.af-accordion-body');
          var otherTrigger = other.querySelector('[data-af-accordion-trigger]');
          if (otherBody) otherBody.style.maxHeight = '0';
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        });

        /* Open clicked (if was closed) */
        if (!wasOpen) {
          item.classList.add('is-open');
          body.style.maxHeight = body.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── Scroll reveal ── */

  function initReveal() {
    if (reducedMotion || !('IntersectionObserver' in window)) {
      return;
    }

    var targets = document.querySelectorAll(
      '.af-page-title-wrap, .af-card, .af-service-item, .af-collection-card, .af-collection-tile, .af-product-card, .af-testimonial-card, .af-feature-card, .af-cta-band, .af-trust, .af-grid-2, .af-gallery-grid, .af-service-list, .af-accordion-item, .af-ba-card, .af-showcase-card, .af-reviews-carousel, .af-gallery-filters, .af-contact-grid, .af-contact-aside-item'
    );

    targets.forEach(function (el, index) {
      if (el.closest('.af-hero')) return; /* hero has its own animation */
      el.classList.add('af-reveal');
      el.style.transitionDelay = String((index % 6) * 50) + 'ms';
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Hero parallax ── */

  function initHeroParallax() {
    if (reducedMotion) {
      return;
    }

    var heroMedia = document.querySelector('.af-hero-media');
    if (!heroMedia) {
      return;
    }

    var ticking = false;

    var sync = function () {
      var y = Math.min(window.scrollY * 0.13, 92);
      heroMedia.style.transform = 'translate3d(0,' + y + 'px,0) scale(1.05)';
      ticking = false;
    };

    var requestSync = function () {
      if (!ticking) {
        window.requestAnimationFrame(sync);
        ticking = true;
      }
    };

    requestSync();
    window.addEventListener('scroll', requestSync, { passive: true });
  }

  /* ── Animated counters ── */

  function initCounters() {
    if (reducedMotion || !('IntersectionObserver' in window)) {
      return;
    }

    var counterEls = document.querySelectorAll('.af-stat-value');
    if (!counterEls.length) return;

    function runCounter(el) {
      if (el.dataset.animated === 'true') return;
      var parsed = parseCounterValue((el.textContent || '').trim());
      if (!parsed) return;
      var start = performance.now();
      var duration = 900;

      var step = function (now) {
        var progress = Math.min((now - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var value = parsed.value * eased;

        var formatted;
        if (parsed.decimals > 0) {
          formatted = value.toFixed(parsed.decimals);
          if (parsed.separator === ',') {
            formatted = formatted.replace('.', ',');
          }
        } else {
          formatted = String(Math.round(value));
        }

        el.textContent = parsed.prefix + formatted + parsed.suffix;
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.dataset.animated = 'true';
        }
      };

      requestAnimationFrame(step);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            runCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counterEls.forEach(function (el) {
      observer.observe(el);
    });
  }

  function parseCounterValue(raw) {
    var normalized = String(raw || '').trim();
    var match = normalized.match(/-?\d+(?:[.,]\d+)?/);
    if (!match || typeof match.index !== 'number') {
      return null;
    }

    var numberToken = match[0];
    var decimals = 0;
    if (numberToken.indexOf('.') > -1) {
      decimals = numberToken.split('.')[1].length;
    } else if (numberToken.indexOf(',') > -1) {
      decimals = numberToken.split(',')[1].length;
    }

    var value = Number(numberToken.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }

    return {
      value: value,
      decimals: decimals,
      separator: numberToken.indexOf(',') > -1 ? ',' : '.',
      prefix: normalized.slice(0, match.index),
      suffix: normalized.slice(match.index + numberToken.length)
    };
  }

  /* ── Quick View modal ── */

  function initQuickView() {
    var modal = document.querySelector('[data-af-quick-modal]');
    var overlay = document.querySelector('[data-af-quick-overlay]');
    if (!modal || !overlay) {
      return;
    }

    /* Move overlay & modal to body so they escape any stacking context.
       Overlay first, modal second = modal on top in DOM order. */
    if (overlay.parentNode !== document.body) document.body.appendChild(overlay);
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    if (quickViewController) quickViewController.abort();
    quickViewController = new AbortController();
    var signal = quickViewController.signal;

    var titleEl = modal.querySelector('[data-af-quick-title]');
    var vendorEl = modal.querySelector('[data-af-quick-vendor]');
    var descriptionEl = modal.querySelector('[data-af-quick-description]');
    var priceEl = modal.querySelector('[data-af-quick-price]');
    var compareEl = modal.querySelector('[data-af-quick-compare-price]');
    var descToggleBtn = modal.querySelector('[data-af-quick-desc-toggle]');
    var stickyPriceEl = modal.querySelector('[data-af-quick-sticky-price]');
    var stickyCompareEl = modal.querySelector('[data-af-quick-sticky-compare]');
    var tagsEl = modal.querySelector('[data-af-quick-tags]');
    var mainImg = modal.querySelector('[data-af-quick-main]');
    var thumbsEl = modal.querySelector('[data-af-quick-thumbs]');
    var variantsEl = modal.querySelector('[data-af-quick-variants]');
    var variantInput = modal.querySelector('[data-af-quick-variant-input]');
    var qtyInput = modal.querySelector('[data-af-quick-qty-input]');
    var addBtn = modal.querySelector('[data-af-quick-add]');
    var productLinks = modal.querySelectorAll('[data-af-quick-product-link]');
    var form = modal.querySelector('form[action*="/cart/add"]');
    var closeButtons = modal.querySelectorAll('[data-af-quick-close]');

    /* Compare (before/after) elements */
    var compareWrap = modal.querySelector('[data-af-quick-compare]');
    var compareStage = modal.querySelector('[data-af-quick-compare-stage]');
    var compareBefore = modal.querySelector('[data-af-quick-compare-before]');
    var compareAfterImg = modal.querySelector('[data-af-quick-compare-after]');
    var compareClip = modal.querySelector('[data-af-quick-compare-clip]');
    var compareDivider = modal.querySelector('[data-af-quick-compare-divider]');
    var compareHandle = modal.querySelector('[data-af-quick-compare-handle]');

    var productCache = {};
    var currentProduct = null;
    var currentVariantIndex = 0;
    var currentImageIndex = 0;
    var isOpen = false;
    var closeTimer = null;
    var modalOpId = 0;
    var previousFocus = null;
    var focusTrapInstance = null;

    function getProductData(id) {
      if (!id) return null;
      if (productCache[id]) return productCache[id];

      var script = document.getElementById('af-product-json-' + id);
      if (!script || !script.textContent) return null;

      try {
        var parsed = JSON.parse(script.textContent);
        productCache[id] = parsed;
        return parsed;
      } catch (e) {
        return null;
      }
    }

    function formatTag(tag) {
      return String(tag || '')
        .replace(/^gama_/, '')
        .replace(/^coleccion_/, '')
        .replace(/^subgama_/, '')
        .replace(/[-_]/g, ' ')
        .trim();
    }

    function closeModal() {
      modalOpId++;
      var myOpId = modalOpId;
      overlay.classList.remove('is-open');
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      isOpen = false;
      setBodyLock(false);

      if (focusTrapInstance) { focusTrapInstance.destroy(); focusTrapInstance = null; }
      if (previousFocus && previousFocus.focus) { previousFocus.focus(); previousFocus = null; }

      closeTimer = window.setTimeout(function () {
        if (modalOpId !== myOpId) return;
        overlay.hidden = true;
        modal.hidden = true;
      }, 230);
    }

    function openModal(product) {
      modalOpId++;
      previousFocus = document.activeElement;
      currentProduct = product;
      currentVariantIndex = 0;
      currentImageIndex = 0;

      if (currentProduct && currentProduct.variants && currentProduct.variants.length) {
        for (var i = 0; i < currentProduct.variants.length; i += 1) {
          if (currentProduct.variants[i].available) {
            currentVariantIndex = i;
            break;
          }
        }
      }

      renderProduct();
      window.clearTimeout(closeTimer);
      overlay.hidden = false;
      modal.hidden = false;
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        if (titleEl) titleEl.focus();
        focusTrapInstance = trapFocus(modal);
      });
      isOpen = true;
      setBodyLock(true);
    }

    function renderProduct() {
      if (!currentProduct) return;

      var images = Array.isArray(currentProduct.images) ? currentProduct.images.filter(Boolean) : [];
      if (!images.length && currentProduct.featured_image) {
        images = [currentProduct.featured_image];
      }

      var variants = Array.isArray(currentProduct.variants) ? currentProduct.variants : [];
      var activeVariant = variants[currentVariantIndex] || variants[0] || null;

      if (titleEl) titleEl.textContent = currentProduct.title || str('product_fallback', 'Producto');
      if (vendorEl) {
        vendorEl.textContent = currentProduct.vendor || '';
        vendorEl.style.display = currentProduct.vendor ? '' : 'none';
      }
      if (descriptionEl) {
        descriptionEl.textContent = currentProduct.description || '';
        descriptionEl.classList.remove('is-expanded');
        /* Show "Ver más" toggle if text is likely clamped (> ~120 chars) */
        if (descToggleBtn) {
          var descText = currentProduct.description || '';
          if (descText.length > 120) {
            descToggleBtn.hidden = false;
            descToggleBtn.textContent = 'Ver m\u00E1s';
          } else {
            descToggleBtn.hidden = true;
          }
        }
      }
      productLinks.forEach(function (link) {
        link.href = currentProduct.url || '#';
      });

      if (mainImg) {
        var mainSrc = images[currentImageIndex] || images[0] || '';
        mainImg.src = mainSrc;
        mainImg.alt = currentProduct.title || str('product_fallback', 'Producto');
      }

      if (thumbsEl) {
        thumbsEl.innerHTML = images
          .map(function (src, index) {
            return (
              '<button type="button" class="af-quick-thumb' +
              (index === currentImageIndex ? ' is-active' : '') +
              '" data-af-quick-image-index="' +
              index +
              '"><img src="' +
              src +
              '" alt="' +
              escapeHtml(currentProduct.title || str('product_fallback', 'Producto')) +
              '" loading="lazy"></button>'
            );
          })
          .join('');
      }

      if (tagsEl) {
        var cleanTags = (Array.isArray(currentProduct.tags) ? currentProduct.tags : [])
          .filter(function (tag) {
            return (
              tag &&
              !/^gama_/i.test(tag) &&
              !/^coleccion_/i.test(tag) &&
              !/^subgama_/i.test(tag) &&
              !/arancha/i.test(tag)
            );
          })
          .slice(0, 3);

        tagsEl.innerHTML = cleanTags
          .map(function (tag) {
            return '<span class="af-tag">' + escapeHtml(formatTag(tag)) + '</span>';
          })
          .join('');
      }

      if (variantsEl) {
        variantsEl.innerHTML = variants
          .map(function (variant, index) {
            var variantImg = variant.image || currentProduct.featured_image || '';
            return (
              '<button type="button" class="af-variant-card' +
              (index === currentVariantIndex ? ' is-selected' : '') +
              (variant.available ? '' : ' is-disabled') +
              '" data-af-quick-variant-index="' +
              index +
              '">' +
              '<img class="af-variant-card__img" src="' + escapeHtml(variantImg) + '" alt="">' +
              '<div>' +
              '<p class="af-variant-card__name">' +
              escapeHtml(variant.title || str('variant_fallback', 'Formato')) +
              '</p>' +
              (variant.sku ? '<p class="af-variant-card__desc">' + escapeHtml(variant.sku) + '</p>' : '') +
              '</div>' +
              '<span class="af-variant-card__price">' +
              money(variant.price || 0) +
              (variant.available ? '' : '<br><small>' + str('sold_out', 'Agotado') + '</small>') +
              '</span>' +
              '</button>'
            );
          })
          .join('');
      }

      if (activeVariant && variantInput) {
        variantInput.value = activeVariant.id;
      }
      if (form) {
        form.dataset.afProductTitle =
          (currentProduct.title || str('product_fallback', 'Producto')) +
          (activeVariant && activeVariant.title && activeVariant.title !== 'Default Title' ? ' \u00B7 ' + activeVariant.title : '');
      }

      if (priceEl) {
        var displayPrice = activeVariant ? activeVariant.price : 0;
        priceEl.textContent = money(displayPrice || 0);
      }
      if (stickyPriceEl) {
        var stickyPrice = activeVariant ? activeVariant.price : 0;
        stickyPriceEl.textContent = money(stickyPrice || 0);
      }
      var comparePrice = activeVariant ? activeVariant.compare_at_price : 0;
      var hasCompare = comparePrice && comparePrice > (activeVariant ? activeVariant.price : 0);
      if (compareEl) {
        if (hasCompare) {
          compareEl.hidden = false;
          compareEl.textContent = money(comparePrice);
        } else {
          compareEl.hidden = true;
          compareEl.textContent = '';
        }
      }
      if (stickyCompareEl) {
        if (hasCompare) {
          stickyCompareEl.hidden = false;
          stickyCompareEl.textContent = money(comparePrice);
        } else {
          stickyCompareEl.hidden = true;
          stickyCompareEl.textContent = '';
        }
      }

      if (addBtn) {
        var isAvailable = activeVariant ? !!activeVariant.available : false;
        addBtn.disabled = !isAvailable;
        addBtn.textContent = isAvailable ? str('add_to_cart', 'A\u00F1adir al carrito') : str('sold_out', 'Agotado');
      }

      if (qtyInput) {
        if (!qtyInput.value || Number(qtyInput.value) < 1) {
          qtyInput.value = '1';
        }
      }

      syncCompare();
    }

    function syncCompare() {
      if (!compareWrap || !currentProduct) {
        if (compareWrap) compareWrap.hidden = true;
        return;
      }
      var beforeSrc = '';
      var afterSrc = '';

      /* 1. Try metafield-sourced before/after images */
      if (currentProduct.before_image && currentProduct.after_image) {
        beforeSrc = currentProduct.before_image;
        afterSrc = currentProduct.after_image;
      }

      /* 2. Fall back to product images (Vercel behaviour: images[1]=before, images[0]=after) */
      if (!beforeSrc || !afterSrc) {
        var images = Array.isArray(currentProduct.images) ? currentProduct.images.filter(Boolean) : [];
        if (images.length >= 2) {
          beforeSrc = images[1] || images[0];
          afterSrc = images[0];
        }
      }

      if (beforeSrc && afterSrc && compareBefore && compareAfterImg) {
        compareBefore.src = afterSrc;
        compareAfterImg.src = beforeSrc;
        compareWrap.hidden = false;
        if (compareClip) compareClip.style.clipPath = 'inset(0 50% 0 0)';
        if (compareDivider) compareDivider.style.left = '50%';
        if (compareHandle) compareHandle.style.left = '50%';
      } else {
        compareWrap.hidden = true;
      }
    }

    overlay.addEventListener('click', closeModal, { signal: signal });
    closeButtons.forEach(function (button) {
      button.addEventListener('click', closeModal, { signal: signal });
    });

    /* Description "Ver más" inside quick view → opens popup with full description */
    if (descToggleBtn && descriptionEl) {
      descToggleBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        var popupOverlay = document.querySelector('[data-af-desc-popup-overlay]');
        var popupEl = document.querySelector('[data-af-desc-popup]');
        var popupBodyEl = document.querySelector('[data-af-desc-popup-body]');
        if (!popupOverlay || !popupEl || !popupBodyEl || !currentProduct) return;
        /* Move popup to body so it sits above the quick-view modal */
        if (popupOverlay.parentNode !== document.body) document.body.appendChild(popupOverlay);
        if (popupEl.parentNode !== document.body) document.body.appendChild(popupEl);
        popupBodyEl.innerHTML = currentProduct.description_html || currentProduct.description || '';
        popupOverlay.classList.add('is-open');
        popupEl.classList.add('is-open');
      }, { signal: signal });
    }

    if (thumbsEl) {
      thumbsEl.addEventListener('click', function (event) {
        var trigger = closestElement(event.target, '[data-af-quick-image-index]');
        if (!trigger) return;
        var index = Number(trigger.getAttribute('data-af-quick-image-index'));
        if (!Number.isFinite(index) || index < 0) return;
        currentImageIndex = index;
        renderProduct();
      }, { signal: signal });
    }

    if (variantsEl) {
      variantsEl.addEventListener('click', function (event) {
        var trigger = closestElement(event.target, '[data-af-quick-variant-index]');
        if (!trigger) return;
        var index = Number(trigger.getAttribute('data-af-quick-variant-index'));
        if (!Number.isFinite(index) || index < 0) return;
        currentVariantIndex = index;
        renderProduct();
      }, { signal: signal });
    }

    modal.addEventListener('click', function (event) {
      var cartTrigger = closestElement(event.target, '[data-af-cart-trigger]');
      if (cartTrigger) {
        closeModal();
      }

      var qtyAction = closestElement(event.target, '[data-af-quick-qty]');
      if (qtyAction && qtyInput) {
        var current = Math.max(1, Number(qtyInput.value) || 1);
        if (qtyAction.getAttribute('data-af-quick-qty') === 'increase') {
          qtyInput.value = String(Math.min(12, current + 1));
        } else {
          qtyInput.value = String(Math.max(1, current - 1));
        }
      }
    }, { signal: signal });

    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var next = Math.round(Number(qtyInput.value) || 1);
        qtyInput.value = String(Math.min(12, Math.max(1, next)));
      }, { signal: signal });
    }

    if (form) {
      form.addEventListener('submit', function () {
        closeModal();
      }, { signal: signal });
    }

    /* Compare slider drag */
    if (compareStage && compareHandle && compareDivider) {
      var compareDragging = false;
      function updateComparePos(clientX) {
        var rect = compareStage.getBoundingClientRect();
        var pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(5, Math.min(95, pct));
        if (compareClip) compareClip.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
        if (compareDivider) compareDivider.style.left = pct + '%';
        if (compareHandle) compareHandle.style.left = pct + '%';
      }
      /* Only start drag from handle or divider — not the entire photo */
      function startCompareDrag(e) {
        e.preventDefault();
        compareDragging = true;
        compareHandle.setPointerCapture(e.pointerId);
      }
      compareHandle.addEventListener('pointerdown', startCompareDrag, { signal: signal });
      compareDivider.addEventListener('pointerdown', startCompareDrag, { signal: signal });
      compareHandle.addEventListener('pointermove', function (e) {
        if (!compareDragging) return;
        updateComparePos(e.clientX);
      }, { signal: signal });
      compareHandle.addEventListener('pointerup', function () { compareDragging = false; }, { signal: signal });
      compareHandle.addEventListener('pointercancel', function () { compareDragging = false; }, { signal: signal });
    }

    document.addEventListener('click', function (event) {
      var trigger = closestElement(event.target, '[data-af-quick-view]');
      if (trigger) {
        event.preventDefault();
        var explicitProductId = trigger.getAttribute('data-product-id');
        var explicitData = getProductData(explicitProductId);
        if (!explicitData) return;
        openModal(explicitData);
        return;
      }

      var card = closestElement(event.target, '[data-af-quick-card]');
      if (!card) return;

      if (closestElement(event.target, 'a, button, input, select, textarea, label, form, [role="button"]')) {
        return;
      }

      var productId = card.getAttribute('data-product-id');
      var data = getProductData(productId);
      if (!data) return;
      openModal(data);
    }, { signal: signal });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) {
        closeModal();
        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      var focused = document.activeElement;
      if (!focused || !focused.matches || !focused.matches('[data-af-quick-card]')) {
        return;
      }

      event.preventDefault();
      var productId = focused.getAttribute('data-product-id');
      var data = getProductData(productId);
      if (!data) return;
      openModal(data);
    }, { signal: signal });
  }

  /* ── Cart Drawer ── */

  function initCartDrawer() {
    var drawer = document.querySelector('[data-af-cart-drawer]');
    var overlay = document.querySelector('[data-af-cart-overlay]');
    var itemContainer = document.querySelector('[data-af-cart-items]');
    var subtotalEl = document.querySelector('[data-af-cart-subtotal]');
    var countContainers = document.querySelectorAll('[data-af-cart-count]');
    var toast = document.querySelector('[data-af-toast]');
    var toastText = document.querySelector('[data-af-toast-text]');
    var closeButtons = document.querySelectorAll('[data-af-cart-close]');
    var addForms = document.querySelectorAll('form[action*="/cart/add"]');

    if (!drawer || !overlay || !itemContainer || !subtotalEl) {
      return;
    }

    /* 1.2 Cleanup previous listeners */
    if (cartDrawerController) cartDrawerController.abort();
    cartDrawerController = new AbortController();
    var signal = cartDrawerController.signal;

    /* 1.1 Cleanup previous MutationObserver */
    if (cartObserverRef) {
      cartObserverRef.disconnect();
      cartObserverRef = null;
    }

    var closeTimer = null;
    var pendingChanges = {};
    var debounceTimer = null;
    var previousFocus = null;
    var focusTrapInstance = null;

    function updateCount(count) {
      countContainers.forEach(function (badge) {
        badge.textContent = String(count);
        badge.style.display = count > 0 ? 'inline-grid' : 'none';
      });
    }

    function getCart() {
      return fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(safeJson);
    }

    function openDrawer() {
      window.clearTimeout(closeTimer);
      previousFocus = document.activeElement;
      overlay.hidden = false;
      drawer.hidden = false;

      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        var firstFocusable = drawer.querySelector('button, [href], input, select, textarea');
        if (firstFocusable) firstFocusable.focus();
        focusTrapInstance = trapFocus(drawer);
      });

      cartState.isOpen = true;
      setBodyLock(true);
    }

    function closeDrawer() {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      cartState.isOpen = false;
      setBodyLock(false);

      if (focusTrapInstance) { focusTrapInstance.destroy(); focusTrapInstance = null; }
      if (previousFocus && previousFocus.focus) { previousFocus.focus(); previousFocus = null; }

      closeTimer = window.setTimeout(function () {
        overlay.hidden = true;
        drawer.hidden = true;
      }, 260);
    }

    function animateCartBump(origin) {
      var cartButton = document.querySelector('.af-cart-link');
      if (cartButton) {
        cartButton.classList.remove('is-bumped');
        void cartButton.offsetWidth;
        cartButton.classList.add('is-bumped');
      }

      if (reducedMotion || !origin || !cartButton) {
        return;
      }

      var originRect = origin.getBoundingClientRect();
      var targetRect = cartButton.getBoundingClientRect();

      var dot = document.createElement('span');
      dot.className = 'af-cart-flydot';
      dot.style.left = originRect.left + originRect.width / 2 + 'px';
      dot.style.top = originRect.top + originRect.height / 2 + 'px';
      document.body.appendChild(dot);

      requestAnimationFrame(function () {
        dot.style.transform =
          'translate3d(' +
          (targetRect.left + targetRect.width / 2 - (originRect.left + originRect.width / 2)) +
          'px,' +
          (targetRect.top + targetRect.height / 2 - (originRect.top + originRect.height / 2)) +
          'px,0) scale(0.35)';
        dot.style.opacity = '0';
      });

      window.setTimeout(function () {
        dot.remove();
      }, 440);
    }

    function renderCart(cart) {
      updateCount(cart.item_count || 0);
      subtotalEl.textContent = money(cart.total_price || 0);
      syncProductCards(cart.items || []);

      if (!cart.items || !cart.items.length) {
        itemContainer.innerHTML = '<div class="af-empty-state" style="margin-top:0.7rem;"><p>' + str('cart_empty', 'Tu carrito est\u00E1 vac\u00EDo.') + '</p></div>';
        return;
      }

      var removeLabel = str('remove_label', 'Eliminar');

      var html = cart.items
        .map(function (item) {
          var imageHtml = '';
          if (item.image) {
            imageHtml =
              '<img src="' +
              item.image +
              '" alt="' +
              escapeHtml(item.product_title || str('product_fallback', 'Producto')) +
              '" loading="lazy">';
          }

          var optionMeta = '';
          if (item.variant_title && item.variant_title !== 'Default Title') {
            optionMeta = '<p class="af-muted" style="font-size:0.76rem; margin:0.15rem 0 0;">' + escapeHtml(item.variant_title) + '</p>';
          }

          return (
            '<article class="af-drawer-item" data-key="' +
            item.key +
            '">' +
            '<a href="' +
            (item.url || '#') +
            '" class="af-drawer-item__media">' +
            imageHtml +
            '</a>' +
            '<div class="af-drawer-item__content">' +
            '<p class="af-drawer-item__title">' +
            escapeHtml(item.product_title || '') +
            '</p>' +
            optionMeta +
            '<p class="af-muted" style="font-size:0.83rem; margin:0.2rem 0 0;">' +
            money(item.final_price || item.price || 0) +
            '</p>' +
            '<div class="af-drawer-item__actions">' +
            '<div class="af-qty-pill">' +
            '<button type="button" data-action="decrease" data-key="' +
            item.key +
            '">\u2212</button>' +
            '<span>' +
            item.quantity +
            '</span>' +
            '<button type="button" data-action="increase" data-key="' +
            item.key +
            '">+</button>' +
            '</div>' +
            '<button class="af-drawer-remove" type="button" data-action="remove" data-key="' +
            item.key +
            '">' + escapeHtml(removeLabel) + '</button>' +
            '</div>' +
            '</div>' +
            '</article>'
          );
        })
        .join('');

      itemContainer.innerHTML = html;
    }

    function syncProductCards(items) {
      var byProduct = {};
      (items || []).forEach(function (item) {
        if (!item || !item.product_id) return;
        var key = String(item.product_id);
        byProduct[key] = (byProduct[key] || 0) + (item.quantity || 0);
      });

      document.querySelectorAll('[data-af-quick-card]').forEach(function (card) {
        var productId = card.getAttribute('data-product-id');
        var count = byProduct[productId] || 0;
        card.classList.toggle('is-in-cart', count > 0);

        var label = card.querySelector('[data-af-in-cart-label]');
        if (!label) return;

        if (count > 0) {
          label.hidden = false;
          label.textContent = str('in_cart_prefix', 'En carrito: ') + count;
        } else {
          label.hidden = true;
          label.textContent = str('in_cart_label', 'En carrito');
        }
      });
    }

    function showToast(text) {
      if (!toast || !toastText) return;
      toastText.textContent = text;
      toast.hidden = false;
      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });
      window.clearTimeout(showToast._timer);
      showToast._timer = window.setTimeout(function () {
        toast.classList.remove('is-visible');
        window.setTimeout(function () {
          toast.hidden = true;
        }, 220);
      }, 1800);
    }

    function refreshCart(openAfter) {
      return getCart()
        .then(function (cart) {
          renderCart(cart);
          if (openAfter) {
            openDrawer();
          }
          return cart;
        })
        .catch(function () {
          if (openAfter) {
            openDrawer();
          }
          return null;
        });
    }

    /* 1.3 Cart operations through queue */

    function changeLine(key, nextQty) {
      return enqueueCartOp(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQty })
        })
          .then(safeJson)
          .then(function (cart) {
            renderCart(cart);
            return cart;
          })
          .catch(function () {
            return refreshCart(false);
          });
      });
    }

    function debouncedChangeLine(key, nextQty) {
      pendingChanges[key] = nextQty;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(function () {
        var batch = pendingChanges;
        pendingChanges = {};
        var keys = Object.keys(batch);
        keys.reduce(function (p, k) {
          return p.then(function () { return changeLine(k, batch[k]); });
        }, Promise.resolve());
      }, 300);
    }

    overlay.addEventListener('click', closeDrawer, { signal: signal });
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeDrawer, { signal: signal });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && cartState.isOpen) {
        closeDrawer();
      }
    }, { signal: signal });

    document.querySelectorAll('[data-af-cart-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        refreshCart(true);
      }, { signal: signal });
    });

    itemContainer.addEventListener('click', function (event) {
      var target = closestElement(event.target, '[data-action][data-key]');
      if (!target || !target.dataset) return;

      var action = target.dataset.action;
      var key = target.dataset.key;
      if (!action || !key) return;

      var card = target.closest('.af-drawer-item');
      if (!card) return;
      var qtyEl = card.querySelector('.af-qty-pill span');
      var currentQty = qtyEl ? Number(qtyEl.textContent || '1') : 1;

      if (action === 'remove') {
        changeLine(key, 0);
      } else if (action === 'increase') {
        debouncedChangeLine(key, currentQty + 1);
      } else if (action === 'decrease') {
        debouncedChangeLine(key, Math.max(0, currentQty - 1));
      }
    }, { signal: signal });

    function bindAddForm(form) {
      if (form.dataset.afBound === 'true') return;
      form.dataset.afBound = 'true';

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = new FormData(form);
        var activeButton = form.querySelector('button[type="submit"]');
        var productTitle = form.dataset.afProductTitle || str('product_fallback', 'Producto');

        enqueueCartOp(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: data
          })
            .then(safeJson)
            .then(function () {
              var isDesktop = window.innerWidth > 767;
              animateCartBump(activeButton || form);
              showToast(productTitle + ' ' + str('added_suffix', 'a\u00F1adido'));
              return refreshCart(isDesktop);
            })
            .catch(function () {
              showToast(str('cart_error', 'Error al a\u00F1adir al carrito'));
            });
        });
      });
    }

    addForms.forEach(bindAddForm);

    /* 1.1 MutationObserver with global tracking */
    if (typeof MutationObserver !== 'undefined') {
      cartObserverRef = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!(node instanceof HTMLElement)) continue;
            if (node.matches && node.matches('form[action*="/cart/add"]')) {
              bindAddForm(node);
            }
            var childForms = node.querySelectorAll ? node.querySelectorAll('form[action*="/cart/add"]') : [];
            for (var k = 0; k < childForms.length; k++) {
              bindAddForm(childForms[k]);
            }
          }
        }
      });
      cartObserverRef.observe(document.body, { childList: true, subtree: true });
    }

    refreshCart(false);
  }

  /* ── Global image error fallback ── */

  document.addEventListener('error', function (event) {
    var target = event.target;
    if (target && target.tagName === 'IMG' && !target.dataset.afFallback) {
      target.dataset.afFallback = 'true';
      target.style.background = 'linear-gradient(135deg, #f5f0eb 0%, #ece4db 100%)';
      target.style.minHeight = '120px';
      target.alt = target.alt || str('image_unavailable', 'Imagen no disponible');
    }
  }, true);

  /* ── Product snippet "Ver más" toggle → opens popup with full description ── */
  function initSnippetToggles() {
    var overlay = document.querySelector('[data-af-desc-popup-overlay]');
    var popup = document.querySelector('[data-af-desc-popup]');
    var popupBody = document.querySelector('[data-af-desc-popup-body]');
    var closeBtn = document.querySelector('[data-af-desc-popup-close]');

    function closePopup() {
      if (overlay) overlay.classList.remove('is-open');
      if (popup) popup.classList.remove('is-open');
      setBodyLock(false);
    }
    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (overlay) overlay.addEventListener('click', closePopup);

    document.querySelectorAll('[data-af-snippet-toggle]').forEach(function (btn) {
      if (btn._afBound) return;
      btn._afBound = true;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (!overlay || !popup || !popupBody) return;
        /* Find the product card and its JSON data */
        var card = btn.closest('[data-af-quick-card]');
        if (card) {
          var pid = card.getAttribute('data-product-id');
          var script = document.getElementById('af-product-json-' + pid);
          if (script) {
            try {
              var data = JSON.parse(script.textContent);
              popupBody.innerHTML = data.description_html || data.description || '';
            } catch (err) {
              popupBody.textContent = btn.previousElementSibling ? btn.previousElementSibling.textContent : '';
            }
          }
        } else {
          /* Fallback: use the sibling snippet text */
          var snippet = btn.previousElementSibling;
          popupBody.textContent = snippet ? snippet.textContent : '';
        }
        overlay.classList.add('is-open');
        popup.classList.add('is-open');
        setBodyLock(true);
      });
    });
  }

  /* ── Product description "Ver más" visibility ── */
  function initProductDescToggle() {
    document.querySelectorAll('[data-af-product-desc]').forEach(function (desc) {
      var btn = desc.nextElementSibling;
      if (!btn || !btn.matches('[data-af-snippet-toggle]')) return;
      if (desc.scrollHeight > desc.clientHeight + 2) {
        btn.style.display = '';
      }
    });
  }


  /* ── Lenis smooth scroll (same config as Vercel: duration 1.05, wheelMultiplier 0.95) ── */
  function initLenis() {
    if (typeof Lenis === 'undefined' || reducedMotion) return;
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    var lenis = new Lenis({
      duration: 1.05,
      smoothWheel: true,
      wheelMultiplier: 0.95,
      touchMultiplier: 1.2
    });
    // Sync with modal lock (same approach as Vercel)
    var syncModalLock = function () {
      if (document.documentElement.classList.contains('af-no-scroll') || document.body.classList.contains('af-no-scroll')) {
        lenis.stop();
      } else {
        lenis.start();
      }
    };
    var observer = new MutationObserver(syncModalLock);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    syncModalLock();

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }

  /* ── Custom cursor (matches Vercel: gold dot + ring with spring physics) ── */
  function initCustomCursor() {
    if (reducedMotion) return;
    var mediaQuery = window.matchMedia('(pointer: fine)');
    if (!mediaQuery.matches) return;

    var dot = document.querySelector('.af-cursor-dot');
    var ring = document.querySelector('.af-cursor-ring');
    if (!dot || !ring) return;

    var mouseX = -100, mouseY = -100;
    var dotX = -100, dotY = -100;
    var ringX = -100, ringY = -100;

    // Spring parameters (snappy response)
    var stiffness = 1200;
    var damping = 55;
    var dotVx = 0, dotVy = 0;
    var ringVx = 0, ringVy = 0;
    var prevTime = 0;

    window.addEventListener('pointermove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
    });

    function tick(time) {
      var dt = prevTime ? Math.min((time - prevTime) / 1000, 0.032) : 0.016;
      prevTime = time;

      // Dot spring (fast, tight)
      var ddx = stiffness * (mouseX - dotX) - damping * dotVx;
      var ddy = stiffness * (mouseY - dotY) - damping * dotVy;
      dotVx += ddx * dt;
      dotVy += ddy * dt;
      dotX += dotVx * dt;
      dotY += dotVy * dt;

      // Ring spring (slightly slower)
      var rdx = (stiffness * 0.7) * (mouseX - ringX) - (damping * 1.1) * ringVx;
      var rdy = (stiffness * 0.7) * (mouseY - ringY) - (damping * 1.1) * ringVy;
      ringVx += rdx * dt;
      ringVy += rdy * dt;
      ringX += ringVx * dt;
      ringY += ringVy * dt;

      dot.style.transform = 'translate3d(' + (dotX - 4) + 'px,' + (dotY - 4) + 'px,0)';
      ring.style.transform = 'translate3d(' + (ringX - 16) + 'px,' + (ringY - 16) + 'px,0)';

      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ── Page transition for link navigation ── */
  function initPageTransitions() {
    if (reducedMotion) return;
    document.addEventListener('click', function (e) {
      /* If another handler already prevented default, skip */
      if (e.defaultPrevented) return;
      var link = e.target.closest('a[href]');
      if (!link) return;
      /* Skip links inside modals/drawers or cart triggers */
      if (link.hasAttribute('data-af-cart-trigger') || link.closest('.af-cart-drawer') || link.closest('.af-quick-modal')) return;
      var href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('http') || link.target === '_blank') return;
      var main = document.getElementById('MainContent');
      if (!main) return;
      e.preventDefault();
      main.style.transition = 'opacity 0.22s ease';
      main.style.opacity = '0';
      setTimeout(function () { window.location.href = href; }, 220);
    });
  }

  /* ── Bootstrap ── */

  document.addEventListener('DOMContentLoaded', function () {
    document.body.classList.add('is-ready');
    initHeader();
    initBeforeAfter();
    initPhotoGallery();
    initReviewsCarousel();
    initAccordion();
    initReveal();
    initHeroParallax();
    initCounters();
    initQuickView();
    initCartDrawer();
    initSnippetToggles();
    initProductDescToggle();
    initLenis();
    initCustomCursor();
    initPageTransitions();
  });

  document.addEventListener('shopify:section:load', function () {
    initHeader();
    initBeforeAfter();
    initPhotoGallery();
    initReviewsCarousel();
    initAccordion();
    initReveal();
    initCounters();
    initQuickView();
    initCartDrawer();
  });
})();
